import { IDP_PROVIDER, IDP_PROTOCOL } from "./idpProviders";
import { inferProviderFromIssuer } from "./inferProviderFromIssuer";

/**
 * @typedef {object} NormalizedSsoProvider
 * @property {number} [id]
 * @property {'sso'} authenticationMethod
 * @property {'oidc' | 'saml'} protocol
 * @property {import('./idpProviders').IdpProviderId} provider
 * @property {string} displayName
 * @property {string | null} buttonLabel
 * @property {string | null} logoUrl
 * @property {string | null} brandColor
 * @property {string} [issuer]
 */

/**
 * Normalize tenant-discovery provider payloads (legacy and enriched API shapes).
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {NormalizedSsoProvider | null}
 */
export function normalizeSsoProvider(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const hasEnrichedShape =
    raw.authenticationMethod === "sso" ||
    typeof raw.provider === "string" ||
    typeof raw.protocol === "string";

  if (hasEnrichedShape) {
    const protocol =
      raw.protocol === IDP_PROTOCOL.SAML
        ? IDP_PROTOCOL.SAML
        : IDP_PROTOCOL.OIDC;
    const providerId = String(raw.provider || "").trim();
    const resolvedProvider = providerId || inferProviderFromIssuer(raw.issuer);

    return {
      id: raw.id != null ? Number(raw.id) : undefined,
      authenticationMethod: "sso",
      protocol,
      provider: resolvedProvider,
      displayName: String(
        raw.displayName || raw.display_name || "Single Sign-On"
      ).trim(),
      buttonLabel: raw.buttonLabel != null ? String(raw.buttonLabel) : null,
      logoUrl: raw.logoUrl != null ? String(raw.logoUrl) : null,
      brandColor: raw.brandColor != null ? String(raw.brandColor) : null,
      issuer: raw.issuer != null ? String(raw.issuer) : undefined,
    };
  }

  const inferred = inferProviderFromIssuer(raw.issuer);
  const displayName = String(
    raw.displayName || raw.display_name || "Single Sign-On"
  ).trim();

  return {
    id: raw.id != null ? Number(raw.id) : undefined,
    authenticationMethod: "sso",
    protocol: IDP_PROTOCOL.OIDC,
    provider: inferred,
    displayName,
    buttonLabel: null,
    logoUrl: null,
    brandColor: null,
    issuer: raw.issuer != null ? String(raw.issuer) : undefined,
  };
}

/**
 * @param {Array<Record<string, unknown>> | null | undefined} providers
 * @returns {NormalizedSsoProvider[]}
 */
export function normalizeSsoProviders(providers) {
  if (!Array.isArray(providers)) {
    return [];
  }

  return providers
    .map((provider) => normalizeSsoProvider(provider))
    .filter(Boolean);
}

/**
 * @param {string | null | undefined} providerId
 * @param {'oidc' | 'saml'} [protocol]
 * @returns {import('./idpProviders').IdpProviderId}
 */
export function resolveUnknownProvider(
  providerId,
  protocol = IDP_PROTOCOL.OIDC
) {
  const value = String(providerId || "").trim();
  if (!value || !Object.values(IDP_PROVIDER).includes(value)) {
    return protocol === IDP_PROTOCOL.SAML
      ? IDP_PROVIDER.GENERIC_SAML
      : IDP_PROVIDER.GENERIC_OIDC;
  }
  return value;
}
